import { useMemo } from 'react';

import { MAX_POPULAR_BRANDS } from '../brands/constants';
import { useBrands } from '../brands/hooks/useBrands';
import { buildPopularBrands } from '../brands/utils';
import { useSearchFilterAggregates } from '../search/useSearchFilterAggregates';

import { getFilteredBrands } from './brandFilterUtils';

export function useBrandsList({
  searchInputValue,
  sellerId,
}: {
  searchInputValue: string;
  sellerId?: number;
}): Array<{
  id?: number;
  name: string;
  slug?: string;
  count?: number;
  isHeading?: boolean;
}> {
  const { data: aggregates } = useSearchFilterAggregates({
    sellerId,
  });
  const { data: brandData, isLoading: isBrandDataLoading } = useBrands();

  const brands = brandData?.data;
  const brandAggregates = aggregates?.data.brands;

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const suggestedBrands = useMemo(() => {
    if (!brands || searchInputValue || !brandAggregates) {
      return [];
    }
    const popularBrands = buildPopularBrands({
      brands,
      brandAggregates,
      maxCount: MAX_POPULAR_BRANDS,
    });

    if (!popularBrands.length) {
      return [];
    }

    return [
      {
        isHeading: true,
        name: 'TopResults',
      },
      ...popularBrands,
    ];
  }, [searchInputValue, isBrandDataLoading, brandAggregates]);

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const aToZBrands = useMemo(() => {
    if (!brands) {
      return [];
    }
    if (searchInputValue) {
      return getFilteredBrands(brands, searchInputValue);
    }
    return [
      { isHeading: true, name: 'Alphabetical' },
      ...Object.values(brands).sort((a, b) => a.name.localeCompare(b.name)),
    ];
  }, [searchInputValue, isBrandDataLoading]);

  return [...suggestedBrands, ...aToZBrands];
}
